/**
 * Icônes au trait de l'identité MAP (ADR 0018, IDENTITE.md §8) et correspondances de présentation : catégorie de mesure → icône,
 * événement → illustration de crise. Aucun chiffre métier ici.
 *
 * Neutralité (CLAUDE.md règle 5) : toutes les catégories ont une icône du même gabarit, même couleur (`currentColor`, encre),
 * même taille ; aucune n'est mise en avant. Une icône posée à côté d'un texte est `aria-hidden` (le texte porte le sens).
 */
import type { MeasureCategory } from "../../engine/src/types.js";
import { ICONS, type IconName } from "./icons.generated.js";

/** SVG d'une icône, taille en px (16 tableaux, 20 boutons, 24 navigation), décoratif (`aria-hidden`). */
export function icon(name: IconName, size: 16 | 18 | 20 | 24 = 20, cls = ""): string {
  return ICONS[name].replace("<svg ", `<svg class="ico${cls ? ` ${cls}` : ""}" width="${size}" height="${size}" focusable="false" `);
}

/** Catégorie du catalogue → icône. « État & fonction publique » n'a pas d'icône livrée : dessin provisoire (ui/assets/icons-ui/). */
export const CATEGORY_ICONS: Record<MeasureCategory, IconName> = {
  retraites: "retraites",
  sante: "sante",
  ecole: "ecole",
  securite: "securite-justice",
  immigration: "immigration",
  fiscalite_menages: "fiscalite-menages",
  fiscalite_entreprises: "fiscalite-entreprises",
  energie: "energie-climat",
  logement: "logement",
  travail: "travail",
  defense: "defense",
  etat: "etat-provisoire",
  europe_dette: "europe-dette",
};

/**
 * Illustrations de crise livrées (brand/crises/, 4 dessins) → événements de content/events.json qui leur correspondent sans
 * ambiguïté. Les autres événements gardent l'icône `crise`, sans illustration (aucune n'est inventée).
 * - prix : « Choc sur les matières premières » ;
 * - grève (voie ferrée, pause) : « Crise sociale : grèves à répétition » (transports) — pas la grève étudiante (universités) ;
 * - épidémie (thermomètre, calendrier) : les deux « Épidémie hivernale sévère » ;
 * - conflit (globe, caisse) : « Guerre aux portes de l'Europe ».
 */
const CRISIS_ART: Record<string, string> = {
  commodities: "crise-prix.svg",
  social: "crise-greve.svg",
  epidemic: "crise-epidemie.svg",
  epidemic_saturation: "crise-epidemie.svg",
  war: "crise-conflit.svg",
};

/** Chemin de l'illustration d'un événement, ou null. Image décorative (le titre de la carte porte le sens) : `alt=""`. */
export function crisisArt(eventId: string): string | null {
  const f = CRISIS_ART[eventId];
  return f ? `assets/crises/${f}` : null;
}
